/**
 * Back to Top Button functionality for Coffee Landing Page
 * Only shows on pages with handle 'coffee' or template 'page.landing'
 */

class BackToTopButton {
  constructor() {
    this.button = null;
    this.scrollThreshold = 300; // Show button after scrolling 300px
    this.isVisible = false;
    
    // Only initialize on coffee landing page
    if (this.shouldInitialize()) {
      this.init();
    }
  }

  shouldInitialize() {
    // Check if we're on the coffee landing page
    const body = document.body;
    const pageHandle = body.getAttribute('data-page-handle');
    const template = body.getAttribute('data-template');
    
    return pageHandle === 'coffee' || template === 'page.landing';
  }

  init() {
    this.createButton();
    this.bindEvents();
    this.checkScrollPosition();
  }

  createButton() {
    // Create the button element
    this.button = document.createElement('button');
    this.button.className = 'back-to-top';
    this.button.setAttribute('aria-label', 'Back to top');
    this.button.setAttribute('title', 'Back to top');
    this.button.type = 'button';
    
    // Add to page
    document.body.appendChild(this.button);
  }

  bindEvents() {
    if (!this.button) return;

    // Click event - smooth scroll to top
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.scrollToTop();
    });

    // Scroll event - show/hide button
    window.addEventListener('scroll', this.throttle(() => {
      this.checkScrollPosition();
    }, 100));

    // Keyboard accessibility
    this.button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.scrollToTop();
      }
    });
  }

  checkScrollPosition() {
    if (!this.button) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const shouldShow = scrollTop > this.scrollThreshold;

    if (shouldShow && !this.isVisible) {
      this.showButton();
    } else if (!shouldShow && this.isVisible) {
      this.hideButton();
    }
  }

  showButton() {
    if (!this.button || this.isVisible) return;
    
    this.button.classList.add('visible');
    this.isVisible = true;
  }

  hideButton() {
    if (!this.button || !this.isVisible) return;
    
    this.button.classList.remove('visible');
    this.isVisible = false;
  }

  scrollToTop() {
    // Smooth scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  // Throttle function to limit scroll event frequency
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  // Public method to destroy the button (useful for cleanup)
  destroy() {
    if (this.button) {
      this.button.remove();
      this.button = null;
      this.isVisible = false;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new BackToTopButton();
});

// Also initialize if script loads after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BackToTopButton();
  });
} else {
  new BackToTopButton();
}